if (!customElements.get("product-form")) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) {
        this.init();
      }
    }

    init() {
      // Get the product form element.
      this.form = this.querySelector(".js-product-form");
      if (!this.form) return;

      // Enable the hidden ID input so that its value can be used.
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;

      // Cache the cart drawer and the Add to Cart button.
      this.cartDrawer = document.querySelector("cart-drawer");
      this.submitBtn = this.querySelector('[name="add"]');

      // Attach the click handler if the button has the popup trigger class.
      if (
        this.submitBtn &&
        this.submitBtn.classList.contains("js-cross-sell-trigger")
      ) {
        this.submitBtn.addEventListener("click", (e) => {
          e.preventDefault();
          // Check for upsell popup first.
          if (this.submitBtn.getAttribute("data-has-upsell") === "true") {
            this.triggerPopup("upsell");
            return;
          }
          // Otherwise, check for cross‑sell popup.
          if (this.submitBtn.getAttribute("data-has-cross-sell") === "true") {
            this.triggerPopup("cross-sell");
          }
        });
      }

      // Bind form submission if After‑ATC behavior is enabled.
      if (theme.settings.afterAtc !== "no-js") {
        this.form.addEventListener("submit", this.handleSubmit.bind(this));
      }
    }

    /**
     * Checks which popup should be triggered and opens it.
     * @param {string} type - "upsell" or "cross-sell"
     */
    triggerPopup(type) {
      const productId = this.submitBtn.getAttribute("data-product-id");
      const variantId = this.getCurrentVariantId();
      const quantity = this.getCurrentQuantity();

      // Build the popup element id.
      const popupId = type === "upsell" ? "upsell-popup-" : "cross-sell-popup-";
      const popupElement = document.getElementById(popupId + productId);

      if (popupElement && typeof popupElement.open === "function") {
        // Update the popup's dataset values.
        popupElement.setAttribute("data-variant-id", variantId);
        popupElement.setAttribute("data-quantity", quantity);

        popupElement.open(productId, quantity);
      } else {
        console.warn(`${type} popup for product ${productId} not found.`);
      }
    }

    /**
     * Retrieves the current variant ID from the hidden input.
     * @returns {string} The current variant ID.
     */
    getCurrentVariantId() {
      const variantInput = this.form.querySelector('input[name="id"]');
      let variantId = this.submitBtn.getAttribute("data-variant-id") || "";
      if (variantInput && variantInput.value) {
        variantId = variantInput.value;
        this.submitBtn.setAttribute("data-variant-id", variantId);
      }
      return variantId;
    }

    /**
     * Retrieves the current quantity from the quantity input.
     * @returns {string|number} The quantity value.
     */
    getCurrentQuantity() {
      const qtyInput = this.form.querySelector(".qty-input__input");
      return qtyInput ? qtyInput.value : 1;
    }

    /**
     * Handles normal form submission (when no popup is triggered).
     * @param {Event} evt - The form submission event.
     */
    async handleSubmit(evt) {
      if (evt.target.id === "product-signup_form") return;

      evt.preventDefault();
      if (this.submitBtn.getAttribute("aria-disabled") === "true") return;
      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable the button while processing.
      this.submitBtn.setAttribute("aria-disabled", "true");
      this.submitBtn.classList.add("is-loading");

      const formData = new FormData(this.form);
      let sections = "cart-icon-bubble";
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer
          .closest(".shopify-section")
          .id.replace("shopify-section-", "")}`;
      }
      formData.append("sections_url", window.location.pathname);
      formData.append("sections", sections);

      const fetchRequestOpts = {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();

        let error =
          typeof data.description === "string"
            ? data.description
            : data.message;
        if (data.errors && typeof data.errors === "object") {
          error = Object.entries(data.errors).map((item) => item[1].join(", "));
        }
        if (data.status) this.setErrorMsgState(error);
        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === "page") {
          setTimeout(() => (window.location.href = theme.routes.cart), 300);
        } else {
          ProductForm.updateCartIcon(data);
          const quickAddDrawer = this.closest("quick-add-drawer");
          if (quickAddDrawer) quickAddDrawer.addedToCart();
          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === "drawer"
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector("cart-items");
              if (cartItems) {
                cartItems.dataset.empty === "true"
                  ? window.location.reload()
                  : cartItems.refresh();
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        if (itemInOldCart) {
          this.dispatchEvent(
            new CustomEvent("on:line-item:change", {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity:
                  itemInOldCart.quantity === data.quantity
                    ? itemInOldCart.quantity
                    : data.quantity,
              },
            })
          );
        } else {
          this.dispatchEvent(
            new CustomEvent("on:cart:add", {
              bubbles: true,
              detail: { cart: newCartData, variantId: data.variant_id },
            })
          );
        }
      } catch (error) {
        this.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: { error: this.errorMsg?.textContent },
          })
        );
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this.submitBtn.classList.add("is-success");
        this.submitBtn.removeAttribute("aria-disabled");
        setTimeout(() => {
          this.submitBtn.classList.remove("is-loading");
          this.submitBtn.classList.remove("is-success");
        }, 1400);
      }
    }

    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById("cart-icon-bubble");
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections["cart-icon-bubble"];
      }
    }

    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector(".js-form-error");
      if (!this.errorMsg) return;
      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = "";
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML("beforeend", "<br>");
          this.errorMsg.insertAdjacentText("beforeend", err);
        });
      }
    }
  }

  customElements.define("product-form", ProductForm);
}
